import { ReceiptText } from 'lucide-react'
import moment from 'moment'
import { useRef, useState } from 'react'
import { Link } from 'react-router'
import useSWR, { useSWRConfig } from 'swr'

import { Calculator } from '@/components/CashRegister/Calculator'
import { CashRegisterNav, CashRegisterNavMenu } from '@/components/CashRegister/CashRegisterNav'
import { PrinterConnector } from '@/components/CashRegister/PrinterConnector'
import { TodayReceiptsSheet } from '@/components/CashRegister/TodayReceiptsSheet'
import { Money } from '@/components/ds'
import { EetWarningBanner } from '@/components/Eet/EetWarningBanner'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { CashRegisterSettings } from '@invo.space/shared'
import { Summary } from '@invo.space/shared'
import { Printer } from '@/lib/printer/printer'

export default function CashRegisterPage() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { mutate } = useSWRConfig()
  const printer = useRef<Printer>(new Printer())
  const { data, error, isLoading } = useSWR<CashRegisterSettings>('/api/v1/cash-register/settings', apiFetcher)

  const today = moment().format('YYYY-MM-DD')
  const { data: todaySummary, mutate: mutateSummary } = useSWR<Summary>(`/api/v1/cash-register/daily-summary?date=${today}`, apiFetcher)

  useSetPageHeader({
    breadcrumbs: [{ href: '/cash-register', children: 'Pokladna' }],
  })

  const notConfigured = !isLoading && !error && data && !data.cashRegisterIdentifier

  return (
    <>
      <title>Pokladna</title>

      <div className="flex flex-col gap-3 md:gap-5 lg:h-[calc(100svh-var(--header-height)-3rem)] lg:min-h-[600px]">
        <EetWarningBanner />

        <div className="-mt-2 flex shrink-0 items-center justify-between gap-2 md:mt-0 md:flex-wrap">
          <CashRegisterNav touch className="hidden md:inline-flex" />
          <CashRegisterNavMenu className="-ml-3 md:hidden" />
          <div className="flex items-center gap-1">
            {!isLoading && !error && <PrinterConnector printer={printer.current} className="border-transparent" />}
            <Button variant="ghost" className="h-10 gap-2 rounded-full px-3 md:h-11 md:px-4" onClick={() => setDrawerOpen(true)}>
              <ReceiptText className="max-sm:hidden" />
              <span className="text-muted-foreground">Dnes</span>
              {todaySummary ? (
                <Money amount={todaySummary.total.total.amount} currency={todaySummary.total.total.currency} animate className="text-foreground" />
              ) : (
                <Skeleton className="h-4 w-16" />
              )}
            </Button>
          </div>
        </div>

        {isLoading && (
          <div className="grid flex-1 gap-8 lg:grid-cols-[minmax(0,1fr)_400px]">
            <Skeleton className="h-96 rounded-xl lg:h-full" />
            <Skeleton className="h-96 rounded-xl lg:h-full" />
          </div>
        )}

        {error && (
          <div className="grid place-items-center gap-3 rounded-xl bg-muted/70 px-8 py-14 text-center">
            <p className="text-base font-medium">Nastavení pokladny se nepodařilo načíst</p>
            <p className="text-sm text-muted-foreground">Zkuste stránku obnovit, nebo zkontrolujte nastavení pokladny.</p>
            <Button variant="outline" asChild>
              <Link to="/cash-register/settings">Nastavení pokladny</Link>
            </Button>
          </div>
        )}

        {notConfigured && (
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-lg bg-warning-bg px-4 py-3 text-sm text-warning">
            <span>Pokladna ještě nemá vyplněné údaje pro účtenku (název, provozovna, sazby DPH).</span>
            <Link to="/cash-register/settings" className="font-medium underline underline-offset-2">
              Doplnit nastavení
            </Link>
          </div>
        )}

        {!isLoading && !error && data && (
          <Calculator printer={printer.current} cashierSettings={data} onReceiptCreated={() => {
              mutateSummary()
              mutate((key) => typeof key === 'string' && key.startsWith('/api/v2/receipts'))
            }} />
        )}
      </div>

      <TodayReceiptsSheet open={drawerOpen} onOpenChange={setDrawerOpen} summary={todaySummary} printer={printer.current} />
    </>
  )
}
